// src/services/asinMonthlyResultsService.ts

import { doc, getDoc, onSnapshot, Unsubscribe, collection, getDocs } from 'firebase/firestore';
import { db } from '@/config/firebase';
import { AsinMonthlyData } from '@/types/asinMonthlyResultsTypes';

/**
 * Service for fetching ASIN monthly results data from Firestore
 * Collection structure: pmt2_allAsinMonthlyResults/{company}/asins/{asin}
 */

/**
 * Fetch ASIN monthly results data for a specific company and ASIN
 * @param companyName - The company name
 * @param asin - The ASIN identifier
 * @returns Promise resolving to the monthly data or null if not found
 */
export const fetchAsinMonthlyResults = async (
  companyName: string,
  asin: string
): Promise<AsinMonthlyData | null> => {
  try {
    if (!companyName || !asin) {
      
      return null;
    }

    // Construct the document path
    const docPath = `pmt2_allAsinMonthlyResults/${companyName}/asins/${asin}`;
    const docRef = doc(db, docPath);
    
    
    
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data() as AsinMonthlyData;
      console.log(`Successfully fetched ASIN monthly results for ${asin}:`, Object.keys(data));
      return data;
    } else {
      
      return null;
    }
  } catch (error) {
    
    throw new Error(`Failed to fetch ASIN monthly results: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

/**
 * Subscribe to real-time updates for ASIN monthly results
 * @param companyName - The company name
 * @param asin - The ASIN identifier
 * @param onData - Callback function called when data changes
 * @param onError - Callback function called when an error occurs
 * @returns Unsubscribe function to stop listening
 */
export const subscribeToAsinMonthlyResults = (
  companyName: string,
  asin: string,
  onData: (data: AsinMonthlyData | null) => void,
  onError: (error: Error) => void
): Unsubscribe => {
  if (!companyName || !asin) {
    
    onData(null);
    return () => {}; // Return empty unsubscribe function
  }

  // Construct the document path
  const docPath = `pmt2_allAsinMonthlyResults/${companyName}/asins/${asin}`;
  const docRef = doc(db, docPath);
  
  

  return onSnapshot(
    docRef,
    (docSnap) => {
      try {
        if (docSnap.exists()) {
          const data = docSnap.data() as AsinMonthlyData;
          console.log(`Real-time update for ASIN ${asin}:`, Object.keys(data));
          onData(data);
        } else {
          
          onData(null);
        }
      } catch (error) {
        
        onError(new Error(`Failed to process data: ${error instanceof Error ? error.message : 'Unknown error'}`));
      }
    },
    (error) => {
      
      onError(new Error(`Subscription error: ${error.message}`));
    }
  );
};

/**
 * Check if ASIN monthly results exist for a company and ASIN
 * @param companyName - The company name
 * @param asin - The ASIN identifier
 * @returns Promise resolving to boolean indicating if data exists
 */
export const checkAsinMonthlyResultsExists = async (
  companyName: string,
  asin: string
): Promise<boolean> => {
  try {
    if (!companyName || !asin) {
      return false;
    }

    const docPath = `pmt2_allAsinMonthlyResults/${companyName}/asins/${asin}`;
    const docRef = doc(db, docPath);
    const docSnap = await getDoc(docRef);
    
    return docSnap.exists();
  } catch (error) {
    
    return false;
  }
};

/**
 * Subscribe to real-time updates for all ASIN monthly results for a company
 * @param companyName - The company name
 * @param onData - Callback function called when data changes
 * @param onError - Callback function called when an error occurs
 * @returns Unsubscribe function to stop listening
 */
export const subscribeToAllAsinMonthlyResults = (
  companyName: string,
  onData: (data: Record<string, AsinMonthlyData> | null) => void,
  onError: (error: Error) => void
): Unsubscribe => {
  if (!companyName) {
    
    onData(null);
    return () => {}; // Return empty unsubscribe function
  }

  // Construct the collection path
  const collectionPath = `pmt2_allAsinMonthlyResults/${companyName}/asins`;
  const collectionRef = collection(db, collectionPath);
  
  

  return onSnapshot(
    collectionRef,
    (querySnapshot) => {
      try {
        const allData: Record<string, AsinMonthlyData> = {};
        
        querySnapshot.forEach((doc) => {
          const asin = doc.id;
          const data = doc.data() as AsinMonthlyData;
          allData[asin] = { ...data, asin };
        });
        
        console.log(`Real-time update for all ASINs in ${companyName}:`, Object.keys(allData));
        onData(Object.keys(allData).length > 0 ? allData : null);
      } catch (error) {
        
        onError(new Error(`Failed to process data: ${error instanceof Error ? error.message : 'Unknown error'}`));
      }
    },
    (error) => {
      
      onError(new Error(`Subscription error: ${error.message}`));
    }
  );
};

/**
 * Fetch all ASIN monthly results data for a specific company
 * @param companyName - The company name
 * @returns Promise resolving to all monthly data or null if not found
 */
export const fetchAllAsinMonthlyResults = async (
  companyName: string
): Promise<Record<string, AsinMonthlyData> | null> => {
  try {
    if (!companyName) {
      
      return null;
    }

    // Construct the collection path
    const collectionPath = `pmt2_allAsinMonthlyResults/${companyName}/asins`;
    const collectionRef = collection(db, collectionPath);
    
    
    
    const querySnapshot = await getDocs(collectionRef);
    
    if (!querySnapshot.empty) {
      const allData: Record<string, AsinMonthlyData> = {};
      
      querySnapshot.forEach((doc) => {
        const asin = doc.id;
        const data = doc.data() as AsinMonthlyData;
        allData[asin] = { ...data, asin };
      });
      
      console.log(`Successfully fetched all ASIN monthly results for ${companyName}:`, Object.keys(allData));
      return allData;
    } else {
      
      return null;
    }
  } catch (error) {
    
    throw new Error(`Failed to fetch all ASIN monthly results: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};